import { AbstractClient, CompositionClient } from "@/app/client";
import { PageOptions } from "@/app/types";
import { UUID } from "crypto";

export interface SMSContactResponse {
  id: UUID;
  user_id: UUID;
  account_id: UUID;
  partner_id?: UUID | null;
  phone_number: string;
  display_name?: string | null;

  // Conversation tracking fields
  last_message_content?: string | null;
  last_message_date?: string | null;
  last_message_direction?: string | null; // "inbound" or "outbound"
  unread_count: number;

  created_at: string;
  updated_at: string;
}

export interface SMSContactList {
  items: SMSContactResponse[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface SMSMessagesByContactResponse {
  chat_id?: string;
  contact?: SMSContactResponse;
  messages: any[]; // Will be SMSMessageResponse[] from existing types
  pre_orders?: any[]; // Pre-orders linked to these messages
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export default class APISMSContactsSub extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  getSMSContacts = async (
    pageOptions?: PageOptions,
    accountId?: UUID,
  ): Promise<SMSContactList> => {
    const params: any = {};
    if (pageOptions) {
      params.page = pageOptions.page;
      params.page_size = pageOptions.size;
    }
    if (accountId) {
      params.account_id = accountId;
    }

    let res = await this._fetch({
      url: "/sms-contacts/",
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getSMSContact = async (contactId: UUID): Promise<SMSContactResponse> => {
    let res = await this._fetch({
      url: `/sms-contacts/${contactId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getContactMessages = async (
    contactId: UUID,
    pageOptions?: PageOptions,
  ): Promise<SMSMessagesByContactResponse> => {
    const params: any = {};
    if (pageOptions) {
      params.page = pageOptions.page;
      params.page_size = pageOptions.size;
    }

    let res = await this._fetch({
      url: `/sms-contacts/${contactId}/messages`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  markContactAsRead = async (contactId: UUID): Promise<SMSContactResponse> => {
    let res = await this._fetch({
      url: `/sms-contacts/${contactId}/mark-read`,
      method: "PUT",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  linkPartnerToContact = async (
    contactId: UUID,
    partnerId: UUID,
  ): Promise<SMSContactResponse> => {
    let res = await this._fetch({
      url: `/sms-contacts/${contactId}/link-partner`,
      method: "POST",
      body: { partner_id: partnerId },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  unlinkPartnerFromContact = async (
    contactId: UUID,
  ): Promise<SMSContactResponse> => {
    let res = await this._fetch({
      url: `/sms-contacts/${contactId}/unlink-partner`,
      method: "POST",
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
